/**
 * ELIA — Feature Flags (React).
 *
 * Consomme `GET /api/feature-flags` au boot et expose un hook simple pour
 * conditionner l'affichage des blocs institutionnels avancés.
 *
 * Usage :
 *   const { isEnabled } = useFeatureFlags();
 *   {isEnabled("ratios_dscr") && <DSCRPanel />}
 *
 * Ou composant :
 *   <FeatureFlag flag="analyst_score"><FullReport /></FeatureFlag>
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const FeatureFlagsContext = createContext({
  flags: {},
  isEnabled: () => false,
  isReady: false,
  refresh: async () => {},
});

// Défauts locaux — utilisés le temps que le backend réponde. Tous les blocs
// institutionnels sont désactivés par défaut (safe MVP-first).
const LOCAL_DEFAULTS = {
  dossier_construction: true,
  elia_assistant: true,
  pdf_generation: true,
  altiora_score: true,
  document_ocr: true,
  geolocation: true,
};

export function FeatureFlagsProvider({ children }) {
  const { user } = useAuth();
  const [flags, setFlags] = useState(LOCAL_DEFAULTS);
  const [isReady, setIsReady] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/feature-flags");
      setFlags({ ...LOCAL_DEFAULTS, ...(data?.flags || {}) });
    } catch {
      // Silencieux : on garde les défauts (institutional = off).
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    if (user) refresh();
    else setIsReady(true);
  }, [user, refresh]);

  const isEnabled = useCallback((key) => Boolean(flags[key]), [flags]);

  const value = useMemo(
    () => ({ flags, isEnabled, isReady, refresh }),
    [flags, isEnabled, isReady, refresh],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

/**
 * Wrapper déclaratif : ne rend `children` que si le flag est actif.
 * `fallback` (optionnel) est rendu à la place sinon.
 */
export function FeatureFlag({ flag, fallback = null, children }) {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag) ? children : fallback;
}
